import React, { useEffect, useState } from 'react'
import { Typography, message, Tooltip } from 'antd'
import { useNavigate } from 'react-router-dom'
import { fetchData } from 'src/Services/MentorServices/ProjectsService'
import moment from 'moment'
import DynamicBadge from 'src/Components/Badges/MentorBadges/DynamicBadges'
import 'src/Pages/LibraryPages/librarypages.scss'

const ACCENTS = ['a-blue', 'a-cyan', 'a-teal', 'a-amber', 'a-emerald', 'a-sky', 'a-navy']

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const BriefcaseIcon = () => (
  <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18" />
  </svg>
)
const ActivityIcon = () => (
  <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M3 12h4l3 8 4-16 3 8h4" />
  </svg>
)
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
)
const BulbIcon = () => (
  <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M9 18h6M10 21h4" />
    <path d="M12 3a6 6 0 0 0-4 10.5c.7.7 1 1.3 1 2.5h6c0-1.2.3-1.8 1-2.5A6 6 0 0 0 12 3z" />
  </svg>
)
const ChartIcon = () => (
  <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M3 3v18h18" />
    <rect x="7" y="11" width="3" height="6" />
    <rect x="12" y="7" width="3" height="10" />
    <rect x="17" y="13" width="3" height="4" />
  </svg>
)
const AwardIcon = () => (
  <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <circle cx="12" cy="9" r="6" />
    <path d="M9 14l-1.5 7L12 18l4.5 3L15 14" />
  </svg>
)
const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" {...stroke} strokeWidth="2.4" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)
const BackIcon = () => (
  <svg viewBox="0 0 24 24" {...stroke} strokeWidth="2.4" aria-hidden="true">
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </svg>
)

const ProjectsPage = () => {
  const token = localStorage.getItem('token')
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedTab, setSelectedTab] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (selectedTab) {
      const fetchProjects = async () => {
        setLoading(true)
        try {
          const completed = selectedTab === 'completed'
          const data = await fetchData(token, completed)
          setProjects(data)
        } catch (error) {
          message.error('Failed to fetch projects')
          console.error(error)
        } finally {
          setLoading(false)
        }
      }
      fetchProjects()
    }
  }, [selectedTab, token])

  const handleManageClick = (id) => {
    navigate(`/projects-Detail/${id}`)
  }

  const tiles = [
    {
      key: 'inflight',
      title: 'Project Inflight',
      badgeKey: 'projectInflight',
      tip: 'View active projects, upcoming deadlines, and required actions.',
      icon: <ActivityIcon />,
      onClick: () => setSelectedTab('inflight'),
    },
    {
      key: 'completed',
      title: 'Completed Project',
      badgeKey: 'projectCompleted',
      tip: 'Review completed projects, outcomes, and final submissions.',
      icon: <CheckIcon />,
      onClick: () => setSelectedTab('completed'),
    },
    {
      key: 'lesson',
      title: 'Lesson Learned',
      badgeKey: 'lessonLearned',
      tip: 'Capture key learnings, challenges, and improvements from each project.',
      icon: <BulbIcon />,
      onClick: () => navigate('/lesson-learned'),
    },
    {
      key: 'progress',
      title: 'Progress Report',
      badgeKey: 'progressReport',
      tip: 'Track project milestones, status updates, and overall progress.',
      icon: <ChartIcon />,
      onClick: () => navigate('/progress-report'),
    },
    {
      key: 'certificate',
      title: 'Project Certificate',
      badgeKey: 'projectCertificate',
      tip: 'View and download project completion certificates.',
      icon: <AwardIcon />,
      onClick: () => navigate('/project-certificate'),
    },
  ]

  const headTitle =
    selectedTab === 'inflight'
      ? 'Project Inflight'
      : selectedTab === 'completed'
      ? 'Completed Projects'
      : 'Projects'
  const headSub =
    selectedTab === 'inflight'
      ? 'Active projects and required actions.'
      : selectedTab === 'completed'
      ? 'Completed projects and outcomes.'
      : 'Your mentored projects.'

  return (
    <div className="lpg">
      <div className="lpg__head">
        <div className="lpg__head-main">
          <span className="lpg__head-ic">
            <BriefcaseIcon />
          </span>
          <div>
            <h1 className="lpg__title">{headTitle}</h1>
            <p className="lpg__sub">{headSub}</p>
          </div>
        </div>
        {selectedTab && (
          <button type="button" className="lpg__back" onClick={() => setSelectedTab(null)}>
            <BackIcon /> Back
          </button>
        )}
      </div>

      {!selectedTab && (
        <div className="lpg__grid">
          {tiles.map((tile, i) => (
            <button
              key={tile.key}
              type="button"
              className={`lpg__card ${ACCENTS[i % ACCENTS.length]}`}
              onClick={tile.onClick}
            >
              <span style={{ position: 'absolute', top: 12, right: 18, zIndex: 1 }}>
                <DynamicBadge dataKey={tile.badgeKey} name="" variant="alternate" />
              </span>
              <span className="lpg__ic">{tile.icon}</span>
              <Tooltip title={tile.tip}>
                <span className="lpg__ct">{tile.title}</span>
              </Tooltip>
              <span className="lpg__go">
                Open <ArrowIcon />
              </span>
            </button>
          ))}
        </div>
      )}

      {selectedTab === 'inflight' &&
        (loading ? (
          <div className="lpg__state">
            <div className="lpg__spin" />
            Loading...
          </div>
        ) : projects.length === 0 ? (
          <div className="lpg__state">No projects found</div>
        ) : (
          <div className="lpg__panel-box">
            {projects.map((project) => (
              <div
                key={project.id ?? project.internshipProjectId}
                className="lpg__panel"
                style={{ marginBottom: 12 }}
              >
                <div style={{ padding: '16px 18px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      flexWrap: 'wrap',
                      marginBottom: 8,
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        fontSize: '1rem',
                        fontWeight: 700,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {project.title}{' '}
                      {project.alertCount > 0 && (
                        <Typography.Text type="danger">({project.alertCount})</Typography.Text>
                      )}
                    </h3>
                    <button
                      type="button"
                      className="lpg__btn-primary"
                      onClick={() => handleManageClick(project.id ?? project.internshipProjectId)}
                    >
                      Manage
                    </button>
                  </div>
                  <p
                    style={{
                      margin: '0 0 8px',
                      color: 'var(--muted)',
                      fontSize: '0.88rem',
                      lineHeight: 1.5,
                    }}
                  >
                    {project.description}
                  </p>
                  <p style={{ margin: 0, color: 'var(--muted)', fontSize: '0.82rem' }}>
                    Start Date: {moment(project.startDate).format('MMMM Do YYYY')}
                  </p>
                  <p style={{ margin: '2px 0 0', color: 'var(--muted)', fontSize: '0.82rem' }}>
                    End Date: {moment(project.endDate).format('MMMM Do YYYY')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ))}

      {selectedTab === 'completed' &&
        (loading ? (
          <div className="lpg__state">
            <div className="lpg__spin" />
            Loading...
          </div>
        ) : projects.length === 0 ? (
          <div className="lpg__state">No projects found</div>
        ) : (
          <div className="lpg__panel-box">
            {projects.map((project) => (
              <div
                key={project.id ?? project.internshipProjectId}
                className="lpg__panel"
                style={{ marginBottom: 12 }}
              >
                <div style={{ padding: '16px 18px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      flexWrap: 'wrap',
                      marginBottom: 8,
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        fontSize: '1rem',
                        fontWeight: 700,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {project.title}
                    </h3>
                    <button
                      type="button"
                      className="lpg__btn-primary"
                      onClick={() => handleManageClick(project.id ?? project.internshipProjectId)}
                    >
                      Manage
                    </button>
                  </div>
                  <p
                    style={{
                      margin: '0 0 8px',
                      color: 'var(--muted)',
                      fontSize: '0.88rem',
                      lineHeight: 1.5,
                    }}
                  >
                    {project.description}
                  </p>
                  <p style={{ margin: 0, color: 'var(--muted)', fontSize: '0.82rem' }}>
                    Start Date: {moment(project.startDate).format('MMMM Do YYYY')}
                  </p>
                  <p style={{ margin: '2px 0 0', color: 'var(--muted)', fontSize: '0.82rem' }}>
                    End Date: {moment(project.endDate).format('MMMM Do YYYY')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ))}
    </div>
  )
}

export default ProjectsPage
